import React, { useEffect, useState } from "react";
import {
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Typography,
  Button,
  ListItemAvatar,
  Avatar,
  Skeleton,
  Badge,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Chip,
  alpha,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { MD5 } from "crypto-js";
import { useConversations } from "../dataaccess/fetchLLMConversations";
import { updateLLMConversationSummary } from "../dataaccess/updateConversationSummary";
import { useLLM } from "llmasaservice-client";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReactMarkdown from "react-markdown";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import EmailIcon from "@mui/icons-material/Email";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AssistantIcon from "@mui/icons-material/Assistant";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import MoodIcon from "@mui/icons-material/Mood";
import NeedIcon from "@mui/icons-material/TrendingUp"; // For "Need"
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { updateLLMConversationIntelligence } from "../dataaccess/updateConversationIntelligence";
import MemoryAndKnowledgeGraph from "./MemoryAndKnowledgeGraph";

const isEmail = (value: string): boolean => /^\S+@\S+\.\S+$/.test(value);

const ConversationsList: React.FC<{
  projectId: string;
  nameFilter: string | undefined;
}> = ({ projectId, nameFilter }) => {
  const {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConversations(projectId, 25);

  //invalidate the react query cache
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const theme = useTheme();

  const [isGeneratingIntelligence, setIsGeneratingIntelligence] =
    useState(false);
  const [tabValue, setTabValue] = useState(0);

  let url = "https://chat.llmasaservice.io/";
  // if the url is localhost or dev.llmasaservice.io, we are in development mode and we should use the dev endpoint
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.llmasaservice.io"
  ) {
    url = "https://chat.llmasaservice.io/dev";
  }

  const { send, idle } = useLLM({ project_id: projectId ?? "", url: url });

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  // Helper to group conversations by created date
  const groupConversations = (conversations: any[]) => {
    const groups: { [key: string]: any[] } = {
      Today: [],
      "This Week": [],
      Older: [],
    };

    const now = new Date();
    conversations
      .filter((conv) => {
        return (
          conv.calls?.items?.length > 0 &&
          (conv.customer?.name ?? "").includes(nameFilter ?? "")
        );
      })
      .forEach((conv) => {
        const createdDate = new Date(conv.createdAt);
        const diffTime = now.getTime() - createdDate.getTime();
        const diffDays = diffTime / (1000 * 3600 * 24);

        if (
          now.getDate() === createdDate.getDate() &&
          now.getMonth() === createdDate.getMonth() &&
          now.getFullYear() === createdDate.getFullYear()
        ) {
          groups["Today"].push(conv);
        } else if (diffDays < 7) {
          groups["This Week"].push(conv);
        } else {
          groups["Older"].push(conv);
        }
      });
    return groups;
  };

  const allConversations =
    data?.pages.flatMap((page) => page.conversations) || [];
  const grouped = groupConversations(allConversations);

  const summarizeConversation = async (conversation: any) => {
    console.log("Summarizing conversation", conversation.id);

    if (!conversation.calls?.items || conversation.calls.items.length === 0) {
      return;
    }

    try {
      // find the most recent call
      const mostRecentCall = conversation.calls.items[0];
      const messages = JSON.parse(mostRecentCall.messages);

      // add the prompt as a user message at the end
      messages.push({
        role: "user",
        content: mostRecentCall.prompt,
      });

      // add the last response
      messages.push({
        role: "assistant",
        content: mostRecentCall.response,
      });

      const llmSummary = await send(
        "Write a 75 word or less summary of what this conversation is about by summarizing what the customer is asking for in the messages.",
        messages,
        [],
        false
      );

      const llmtitle = await send(
        "Respond with ONLY a three word title for this conversation, no formatting or markup.",
        messages,
        [],
        false
      );

      if (llmSummary && llmtitle) {
        // update the conversation with the summary
        await updateLLMConversationSummary(
          conversation.id,
          "active",
          removeLeadingAndTrailingQuotes(llmSummary as string),
          removeLeadingAndTrailingQuotes(llmtitle as string)
        );
        queryClient.invalidateQueries({
          queryKey: ["conversations", projectId],
        });
      }
    } catch (err) {
      console.error("Failed to parse messages", err);
    }
  };

  const removeLeadingAndTrailingQuotes = (str: string) => {
    return str
      .replace(/^"(.*)"$/, "$1")
      .replace("(cached)", "")
      .replace("(PII redacted)", "")
      .trim();
  };

  /* removed

     - Identify the overall sentiment of each message (e.g., “positive,” “negative,” or “neutral”).
   - Include a brief rationale.
      "sentimentAnalysis": [
      {
        "messageId": "<unique identifier>",
        "textExcerpt": "<message text or a short excerpt>",
        "sentiment": "<positive|negative|neutral>",
        "rationale": "<brief explanation>"
      }
      // ... repeat for additional messages
    ],

    */

  const analyzeConversation = async (conversation: any) => {
    setIsGeneratingIntelligence(true);
    try {
      const newAnalyzeInstructions = `
SYSTEM INSTRUCTION

You are a Conversation Analysis Assistant. For every conversation, do the following:

1. **Company Details from Email Domains**
   - If an email address is present, parse out the domain (e.g., “acme.com”).
   - Provide publicly available company information from that domain if known (e.g., “Acme Inc.” and size). 

2. **Individual Detail from Email**
   - If an email address is present
   - Provide publicly available personal information from that email, e.g. their linked in profile

3. **Sentiment Analysis**
   - Identify the overall sentiment of the conversation

4. **BANT Analysis**
   - Check for Budget, Authority, Need, and Timeline factors.
   - For each factor, provide one of the following:
     - “Yes,” “No,” or “Not Specified” (if no information is available).
     - A single sentence summary of the specific information available
   - Optionally, include any helpful clarifications or relevant details.

5. **Output Format**
   - Provide your final analysis **strictly** as a single JSON object matching the schema below.
   - No additional text, code blocks, explanations, or formatting outside the JSON.

**Required Schema** (with all listed properties):

\`\`\`json
{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "$id": "https://example.com/analysis.schema.json",
  "title": "Analysis Schema",
  "type": "object",
  "properties": {
    "analysis": {
      "type": "object",
      "properties": {
        "companyDetails": {
          "type": "array",
          "items": {
            "type": "object",
            "properties": {
              "emailAddress": {
                "type": "string",
                "format": "email"
              },
              "domain": {
                "type": "string"
              },
              "companyName": {
                "type": "string"
              },
              "companySize": {
                "type": "string"
              },
              "additionalInfo": {
                "type": "string"
              }
            },
            "required": [
              "emailAddress",
              "domain"
            ],
            "additionalProperties": false
          }
        },
        "personalDetails": {
          "type": "array",
          "items": {
            "type": "object",
            "properties": {
              "emailAddress": {
                "type": "string",
                "format": "email"
              },
              "name": {
                "type": "string"
              },
              "linkedIn": {
                "type": "string"
              },
              "additionalInfo": {
                "type": "string"
              }
            },
            "required": [
              "emailAddress"
            ],
            "additionalProperties": false
          }
        },
        "sentimentOverall": {
          "type": "string",
          "enum": [
            "positive",
            "negative",
            "neutral"
          ]
        },
        "bantAnalysis": {
          "type": "object",
          "properties": {
            "budget": {
              "type": "string",
              "enum": [
                "Yes",
                "No",
                "Not Specified"
              ]
            },
            "budgetDetail": {
              "type": "string"
            },
            "authority": {
              "type": "string",
              "enum": [
                "Yes",
                "No",
                "Not Specified"
              ]
            },
            "authorityDetail": {
              "type": "string"
            },
            "need": {
              "type": "string",
              "enum": [
                "Yes",
                "No",
                "Not Specified"
              ]
            },
            "needDetail": {
              "type": "string"
            },
            "timeline": {
              "type": "string",
              "enum": [
                "Yes",
                "No",
                "Not Specified"
              ]
            },
            "timelineDetail": {
              "type": "string"
            },
            "details": {
              "type": "string"
            }
          },
          "required": [
            "budget",
            "budgetDetail",
            "authority",
            "authorityDetail",
            "need",
            "needDetail",
            "timeline",
            "timelineDetail",
            "details"
          ],
          "additionalProperties": false
        }
      },
      "required": [
        "companyDetails",
        "personalDetails",
        "sentimentOverall",
        "bantAnalysis"
      ],
      "additionalProperties": false
    }
  },
  "required": [
    "analysis"
  ],
  "additionalProperties": false
}
\`\`\`

**EXAMPLE:**
\`\`\`json
{
  "analysis": {
    "companyDetails": [
      {
        "emailAddress": "<user@domain.com>",
        "domain": "<domain.com>",
        "companyName": "<if known>",
        "companySize": "<if known>",
        "additionalInfo": "<any relevant public info>",
      },
      // ... repeat if multiple email domains appear
    ],
    "personalDetails": [
      {
        "emailAddress": "<user@domain.com>",
        "name": ",users name guessed or provided>",
        "linkedIn": "<linkedIn profile>",
        "additionalInfo": "<any relevant public info>"
      },
      // ... repeat if multiple email addresses appear
    ],
    "sentimentOverall": "<positive|negative|neutral>",
    "bantAnalysis": {
      "budget": "<Yes|No|Not Specified>",
      "budgetDetail": "<budget details>",
      "authority": "<Yes|No|Not Specified>",
      "authorityDetail": "<authority details>",
      "need": "<Yes|No|Not Specified>",
      "needDetail": "<need details>",
      "timeline": "<Yes|No|Not Specified>",
      "timelineDetail": "<timeline details>",
      "details": "<any clarifications or relevant info>",
    }
  }
}\`\`\`

**Your final response must be valid JSON conforming to the schema above. Output nothing else.**

END OF SYSTEM INSTRUCTION
`;

      let prompt = `Using the SYSTEM INSTRUCTION specified, analyze this conversation and respond in the JSON structure specified.
    
    The users email address if not specified in the conversation is ${
      conversation?.emailAddress ?? "not known"
    }. 
    
    `;

      // add the conversation messages to the prompt
      const convo =
        conversation.calls?.items?.map((call: any, index: number) => [
          {
            id: "prompt" + index,
            role: "user",
            content: call?.prompt,
          },
          {
            id: "response" + index,
            role: "assistant",
            content: call.response,
          },
        ]) || [];

      prompt += JSON.stringify(convo, null, 2);

      try {
        const analysis = await send(
          prompt,
          [
            {
              role: "system",
              content: newAnalyzeInstructions,
            },
          ],
          [],
          false,
          false
        );

        console.log("Analysis response:", analysis);

        let parsedAnalysis: any = null;
        try {
          // Attempt to parse the JSON response with more robust cleanup
          let cleanedJson = (analysis as string)
            .replace(/```json/g, "") // Remove json code block markers
            .replace(/```/g, "") // Remove generic code block markers
            .replace(/\(PII redacted\)/g, "") // Remove PII notice
            .replace(/\(cached\)/g, ""); // Remove cached notice

          // Fix common JSON structure issues
          // This regex looks for a property with incomplete value followed by another property
          cleanedJson = cleanedJson.replace(
            /"rationale": "([^"]*)",\s*"(\w+)":/g,
            '"rationale": "$1"},{"$2":'
          );

          // Make sure we have proper structure for BANT analysis
          if (
            !cleanedJson.includes('"bantAnalysis"') &&
            cleanedJson.includes('"budget"')
          ) {
            cleanedJson = cleanedJson.replace(
              /"budget":/g,
              '"bantAnalysis": {"budget":'
            );
            // Add closing brace for bantAnalysis if needed
            if (!cleanedJson.match(/}\s*}\s*}\s*$/)) {
              cleanedJson = cleanedJson.replace(/}\s*}\s*$/, "}}}");
            }
          }

          // Sometimes the sentiment overall is missing - add a default if that's the case
          if (!cleanedJson.includes('"sentimentOverall"')) {
            cleanedJson = cleanedJson.replace(
              /"sentimentAnalysis": \[\s*{/g,
              '"sentimentAnalysis": [{'
            );
            cleanedJson = cleanedJson.replace(
              /}\s*\]\s*,\s*"bantAnalysis"/g,
              '}],"sentimentOverall": "neutral","bantAnalysis"'
            );
          }

          console.log("Cleaned JSON:", cleanedJson);

          parsedAnalysis = JSON.parse(cleanedJson);
        } catch (e) {
          // If it's still not valid JSON, try to extract just the BANT analysis
          console.error("Failed to parse Cleaned JSON", e);

          try {
            // Try to extract just the BANT section as a fallback
            const bantMatch = (analysis as string).match(
              /"budget"[^}]+},?\s*"authority"[^}]+},?\s*"need"[^}]+},?\s*"timeline"[^}]+}/
            );
            if (bantMatch) {
              parsedAnalysis = {
                analysis: {
                  bantAnalysis: JSON.parse(`{${bantMatch[0]}}`),
                },
              };
            } else {
              // If we can't even extract BANT, store as text
              parsedAnalysis = { rawText: analysis };
            }
          } catch (bantError) {
            parsedAnalysis = { rawText: analysis };
          }
        }

        await updateLLMConversationIntelligence(
          conversation.id,
          null,
          JSON.stringify(parsedAnalysis, null, 2)
        );
        queryClient.invalidateQueries({
          queryKey: ["conversations", projectId],
        });
      } catch (error) {
        console.error("Analysis failed:", error);
      }
    } finally {
      setIsGeneratingIntelligence(false);
    }
  };

  const CodeBlock = ({ node, className, children, style, ...props }: any) => {
    const match = /language-(\w+)/.exec(className || "");

    return match ? (
      <>
        <div
          style={{
            border: 0,
            padding: 0,
            height: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{match ? match[1] : "Unknown"}</span>
          <button
            onClick={() => {
              //todo
            }}
            className="copy-button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 320 320"
              fill="currentColor"
              style={{
                width: "16px",
                height: "16px",
                margin: 0,
                border: 0,
              }}
            >
              <path
                d="M35,270h45v45c0,8.284,6.716,15,15,15h200c8.284,0,15-6.716,15-15V75c0-8.284-6.716-15-15-15h-45V15
		c0-8.284-6.716-15-15-15H35c-8.284,0-15,6.716-15,15v240C20,263.284,26.716,270,35,270z M280,300H110V90h170V300z M50,30h170v30H95
		c-8.284,0-15,6.716-15,15v165H50V30z"
              />
              <path d="M155,120c-8.284,0-15,6.716-15,15s6.716,15,15,15h80c8.284,0,15-6.716,15-15s-6.716-15-15-15H155z" />
              <path d="M235,180h-80c-8.284,0-15,6.716-15,15s6.716,15,15,15h80c8.284,0,15-6.716,15-15S243.284,180,235,180z" />
              <path
                d="M235,240h-80c-8.284,0-15,6.716-15,15c0,8.284,6.716,15,15,15h80c8.284,0,15-6.716,15-15C250,246.716,243.284,240,235,240z
		"
              />
            </svg>
          </button>
        </div>
        <SyntaxHighlighter
          style={materialDark}
          PreTag="div"
          language={match[1]}
          {...props}
        >
          {String(children).replace(/\n$/, "")}
        </SyntaxHighlighter>
      </>
    ) : (
      <code className={className ? className : ""} {...props}>
        {children}
      </code>
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box p={2}>
      <Typography variant="h5">Conversations</Typography>

      {Object.entries(grouped).map(([groupName, conversations]) => (
        <Box key={groupName} mb={3}>
          {conversations.length > 0 && (
            <>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                {groupName}
              </Typography>
              {!idle && (
                <Typography variant="caption">generating summary...</Typography>
              )}
              <List>
                {conversations.map((conversation: any) => (
                  <React.Fragment key={conversation.id}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <ListItem alignItems="flex-start">
                          <ListItemAvatar>
                            <Box
                              mr={2}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              {conversation.customer?.name &&
                              isEmail(conversation.customer.name) ? (
                                <Badge
                                  badgeContent={
                                    conversation.calls?.items?.length || 0
                                  }
                                  color="primary"
                                >
                                  <Avatar
                                    alt={conversation.customer.name}
                                    src={`https://www.gravatar.com/avatar/${MD5(
                                      conversation.customer.name
                                        .trim()
                                        .toLowerCase()
                                    )}?s=40&r=pg&d=identicon`}
                                    sx={{ width: 40, height: 40 }} // full size avatar
                                  />
                                </Badge>
                              ) : (
                                <Badge
                                  badgeContent={
                                    conversation.calls?.items?.length || 0
                                  }
                                  color="primary"
                                >
                                  <Skeleton
                                    variant="circular"
                                    width={40}
                                    height={40}
                                  />
                                </Badge>
                              )}

                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(3, auto)",
                                  gap: 1,
                                  mt: 1,
                                }}
                              >
                                {(conversation?.ctaClicked ?? 0) > 0 && (
                                  <Tooltip title="Call to Action Clicked">
                                    <Avatar
                                      sx={{
                                        bgcolor: theme.palette.warning.main,
                                        width: 25,
                                        height: 25,
                                      }}
                                      variant="rounded"
                                    >
                                      <ContactMailIcon />
                                    </Avatar>
                                  </Tooltip>
                                )}
                                {(conversation?.thumbsdownClicked ?? 0) > 0 && (
                                  <Tooltip title="Thumbs Down Clicked">
                                    <Avatar
                                      sx={{
                                        bgcolor: theme.palette.warning.main,
                                        width: 25,
                                        height: 25,
                                      }}
                                      variant="rounded"
                                    >
                                      <ThumbDownIcon />
                                    </Avatar>
                                  </Tooltip>
                                )}
                                {(conversation?.actionClicked ?? 0) > 0 && (
                                  <Tooltip title="Response Action Clicked">
                                    <Avatar
                                      sx={{
                                        bgcolor: theme.palette.success.main,
                                        width: 25,
                                        height: 25,
                                      }}
                                      variant="rounded"
                                    >
                                      <AdsClickIcon />
                                    </Avatar>
                                  </Tooltip>
                                )}
                                {(conversation?.thumbsupClicked ?? 0) > 0 && (
                                  <Tooltip title="Thumbs Up Clicked">
                                    <Avatar
                                      sx={{
                                        bgcolor: theme.palette.success.main,
                                        width: 25,
                                        height: 25,
                                      }}
                                      variant="rounded"
                                    >
                                      <ThumbUpIcon />
                                    </Avatar>
                                  </Tooltip>
                                )}
                                {(conversation?.copyClicked ?? 0) > 0 && (
                                  <Tooltip title="Copy Clicked">
                                    <Avatar
                                      sx={{
                                        bgcolor: theme.palette.success.main,
                                        width: 25,
                                        height: 25,
                                      }}
                                      variant="rounded"
                                    >
                                      <ContentCopyIcon />
                                    </Avatar>
                                  </Tooltip>
                                )}
                                {(conversation?.emailClicked ?? 0) > 0 && (
                                  <Tooltip title="Email Clicked">
                                    <Avatar
                                      sx={{
                                        bgcolor: theme.palette.success.main,
                                        width: 25,
                                        height: 25,
                                      }}
                                      variant="rounded"
                                    >
                                      <EmailIcon />
                                    </Avatar>
                                  </Tooltip>
                                )}
                                {(conversation?.saveClicked ?? 0) > 0 && (
                                  <Tooltip title="Save Clicked">
                                    <Avatar
                                      sx={{
                                        bgcolor: theme.palette.success.main,
                                        width: 25,
                                        height: 25,
                                      }}
                                      variant="rounded"
                                    >
                                      <SaveAltIcon />
                                    </Avatar>
                                  </Tooltip>
                                )}
                                {(conversation?.suggestionClicked ?? 0) > 0 && (
                                  <Tooltip title="Suggestion Clicked">
                                    <Avatar
                                      sx={{
                                        bgcolor: theme.palette.success.main,
                                        width: 25,
                                        height: 25,
                                      }}
                                      variant="rounded"
                                    >
                                      <AssistantIcon />
                                    </Avatar>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          </ListItemAvatar>

                          <ListItemText
                            primary={
                              <>
                                {conversation.customer?.name &&
                                isEmail(conversation.customer.name) ? (
                                  <a
                                    href={`mailto:${conversation.customer.name}`}
                                    style={{
                                      textDecoration: "none",
                                      color: "inherit",
                                    }}
                                  >
                                    {conversation.customer.name}
                                  </a>
                                ) : (conversation.customer?.name ?? "") !==
                                  "" ? (
                                  conversation.customer.name
                                ) : (
                                  "Anonymous"
                                )}
                                &nbsp;{" "}
                                {conversation?.calls?.items &&
                                  conversation?.calls?.items.length > 0 && (
                                    <Tooltip title="Country">
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={
                                          conversation?.calls?.items[0]?.country
                                        }
                                      />
                                    </Tooltip>
                                  )}
                                {conversation?.timezone &&
                                  conversation?.timezone !== "unknown" && (
                                    <Tooltip title="User's timezone">
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={conversation?.timezone
                                          .replace("_", " ")
                                          .toLowerCase()}
                                      />
                                    </Tooltip>
                                  )}
                                {conversation?.language &&
                                  conversation?.language !== "unknown" && (
                                    <Tooltip title="User's language">
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={conversation?.language
                                          .replace("_", " ")
                                          .toLowerCase()}
                                      />
                                    </Tooltip>
                                  )}{" "}
                                - {conversation.title} (
                                {new Date(
                                  conversation.createdAt
                                ).toLocaleDateString()}
                                )
                              </>
                            }
                            secondary={
                              <>
                                {conversation.summary}
                                <br />
                                {/*
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={async () => {
                                    await updateLLMConversationSummary(
                                      conversation.id,
                                      "archived",
                                      conversation.summary || "",
                                      conversation.title || ""
                                    );
                                    queryClient.invalidateQueries({
                                      queryKey: ["conversations", projectId],
                                    });
                                  }}
                                >
                                  Archive
                                </Button>
                                &nbsp;|&nbsp; */}
                                <Button
                                  size="small"
                                  variant="text"
                                  disabled={!conversation.customer?.id}
                                  onClick={() =>
                                    navigate(
                                      `/customers?customerId=${conversation.customer?.id}`
                                    )
                                  }
                                >
                                  View Customer
                                </Button>
                                &nbsp;|&nbsp;
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={async () => {
                                    await summarizeConversation(conversation);
                                  }}
                                  disabled={!idle}
                                >
                                  {!conversation.summary ||
                                  conversation.summary === ""
                                    ? "Summarize"
                                    : "Refresh summary"}
                                </Button>
                              </>
                            }
                          ></ListItemText>
                        </ListItem>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          sx={{ display: "flex", flexDirection: "row", gap: 2 }}
                        >
                          {(() => {
                            let parsedIntelligence = null;
                            try {
                              parsedIntelligence = conversation.intelligence
                                ? JSON.parse(conversation.intelligence)
                                : null;
                            } catch (e) {
                              console.error(
                                "Failed to parse conversation intelligence:",
                                e
                              );
                            }

                            return (
                              <>
                                {/* Left side: Conversation (2/3 width) */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                    width: "50%",
                                  }}
                                >
                                  <Typography variant="h6" gutterBottom>
                                    Conversation
                                  </Typography>
                                  {conversation.calls?.items?.map(
                                    (call: any, index: number) => (
                                      <Box
                                        key={index}
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 1,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            bgcolor:
                                              theme.palette.background.paper,
                                            p: 1,
                                            borderRadius: 2,
                                            maxWidth: "90%",
                                          }}
                                        >
                                          <Typography
                                            component="div"
                                            variant="body2"
                                          >
                                            <ReactMarkdown>
                                              {call?.prompt?.includes(
                                                "CONTEXT:"
                                              )
                                                ? call?.prompt
                                                    ?.split("CONTEXT:")[0]
                                                    ?.split("PROMPT:")[1] || ""
                                                : call?.prompt}
                                            </ReactMarkdown>
                                          </Typography>
                                        </Box>
                                        <Box
                                          sx={{
                                            bgcolor:
                                              theme.palette.background.paper,
                                            p: 1,
                                            borderRadius: 2,
                                            maxWidth: "90%",
                                            alignSelf: "flex-end",
                                          }}
                                        >
                                          <Typography
                                            component="div"
                                            variant="body2"
                                            sx={{ mb: 1, overflowY: "auto" }}
                                          >
                                            <ReactMarkdown
                                              remarkPlugins={[remarkGfm]}
                                              rehypePlugins={[rehypeRaw]}
                                              components={{
                                                code: CodeBlock,
                                              }}
                                            >
                                              {call.response}
                                            </ReactMarkdown>
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )
                                  )}
                                </Box>

                                {/* Right side: Tabbed Intelligence Section (1/2 width) */}
                                <Box
                                  sx={{
                                    width: "50%",
                                    bgcolor: theme.palette.background.default,
                                    p: 2,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Tabs
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    variant="fullWidth"
                                    sx={{ mb: 2 }}
                                  >
                                    <Tab label="Conversation Intelligence" />
                                    <Tab label="Customer Memories" />
                                  </Tabs>

                                  {/* Intelligence Tab */}
                                  {tabValue === 0 && (
                                    <Box>
                                      <Typography variant="h6" gutterBottom>
                                        Conversation Intelligence (BETA)
                                      </Typography>

                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() =>
                                          analyzeConversation(conversation)
                                        }
                                        disabled={!idle}
                                        sx={{ mb: 2 }}
                                      >
                                        {!parsedIntelligence?.analysis
                                          ? "Analyze now"
                                          : "Regenerate"}
                                      </Button>

                                      {isGeneratingIntelligence && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <CircularProgress size={20} />
                                          <Typography variant="body2">
                                            Generating insights...
                                          </Typography>
                                        </Box>
                                      )}

                                      {!parsedIntelligence?.analysis &&
                                      !isGeneratingIntelligence ? (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontStyle: "italic" }}
                                        >
                                          Click Analyze to generate insights
                                        </Typography>
                                      ) : (
                                        <>
                                          {/* Company Info Card */}
                                          {parsedIntelligence?.analysis
                                            ?.companyDetails?.[0] && (
                                            <Box
                                              sx={{
                                                p: 2,
                                                bgcolor:
                                                  theme.palette.background.paper,
                                                borderRadius: 1,
                                                boxShadow: 1,
                                              }}
                                            >
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  fontWeight: "bold",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                  mb: 1,
                                                }}
                                              >
                                                <BusinessIcon fontSize="small" />{" "}
                                                Company
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  gap: 0.5,
                                                }}
                                              >
                                                <Typography variant="body2">
                                                  <strong>Name:</strong>{" "}
                                                  {parsedIntelligence?.analysis
                                                    .companyDetails[0]
                                                    .companyName || "Unknown"}
                                                </Typography>
                                                {parsedIntelligence?.analysis
                                                  .companyDetails[0].domain && (
                                                  <Typography variant="body2">
                                                    <strong>Domain:</strong>{" "}
                                                    {
                                                      parsedIntelligence?.analysis
                                                        .companyDetails[0].domain
                                                    }
                                                  </Typography>
                                                )}
                                                {parsedIntelligence?.analysis
                                                  .companyDetails[0]
                                                  .companySize && (
                                                  <Typography variant="body2">
                                                    <strong>Company size:</strong>{" "}
                                                    {
                                                      parsedIntelligence?.analysis
                                                        .companyDetails[0]
                                                        .companySize
                                                    }
                                                  </Typography>
                                                )}
                                                {parsedIntelligence?.analysis
                                                  .companyDetails[0]
                                                  .additionalInfo && (
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      mt: 1,
                                                      color:
                                                        theme.palette.text
                                                          .secondary,
                                                    }}
                                                  >
                                                    {
                                                      parsedIntelligence?.analysis
                                                        .companyDetails[0]
                                                        .additionalInfo
                                                    }
                                                  </Typography>
                                                )}
                                              </Box>
                                            </Box>
                                          )}

                                          {/* Personal Details Card */}
                                          {parsedIntelligence?.analysis
                                            ?.personalDetails?.[0] && (
                                            <Box
                                              sx={{
                                                mt: 2,
                                                p: 2,
                                                bgcolor:
                                                  theme.palette.background.paper,
                                                borderRadius: 1,
                                                boxShadow: 1,
                                              }}
                                            >
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  fontWeight: "bold",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                  mb: 1,
                                                }}
                                              >
                                                <PersonIcon fontSize="small" />{" "}
                                                Contact Person
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  gap: 0.5,
                                                }}
                                              >
                                                <Typography variant="body2">
                                                  <strong>Name:</strong>{" "}
                                                  {parsedIntelligence?.analysis
                                                    .personalDetails[0].name ||
                                                    "Unknown"}
                                                </Typography>
                                                {parsedIntelligence?.analysis
                                                  .personalDetails[0]
                                                  .emailAddress && (
                                                  <Typography variant="body2">
                                                    <strong>Email:</strong>{" "}
                                                    {
                                                      parsedIntelligence?.analysis
                                                        .personalDetails[0]
                                                        .emailAddress
                                                    }
                                                  </Typography>
                                                )}
                                                {parsedIntelligence?.analysis
                                                  .personalDetails[0].linkedIn && (
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      mt: 1,
                                                    }}
                                                  >
                                                    <LinkedInIcon
                                                      sx={{
                                                        mr: 1,
                                                        color: "#0a66c2",
                                                      }}
                                                    />
                                                    <a
                                                      href={
                                                        parsedIntelligence?.analysis
                                                          .personalDetails[0]
                                                          .linkedIn
                                                      }
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      style={{
                                                        textDecoration: "none",
                                                        color: "#0a66c2",
                                                      }}
                                                    >
                                                      LinkedIn Profile
                                                    </a>
                                                  </Box>
                                                )}
                                              </Box>
                                            </Box>
                                          )}

                                          {/* Sentiment Indicator */}
                                          {parsedIntelligence?.analysis
                                            ?.sentimentOverall && (
                                            <Box sx={{ mt: 2 }}>
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  fontWeight: "bold",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                }}
                                              >
                                                <MoodIcon fontSize="small" />{" "}
                                                Sentiment
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  mt: 1,
                                                  p: 1.5,
                                                  bgcolor:
                                                    parsedIntelligence?.analysis
                                                      .sentimentOverall ===
                                                    "positive"
                                                      ? alpha(
                                                          theme.palette.success
                                                            .main,
                                                          0.1
                                                        )
                                                      : parsedIntelligence?.analysis
                                                          .sentimentOverall ===
                                                        "negative"
                                                      ? alpha(
                                                          theme.palette.error.main,
                                                          0.1
                                                        )
                                                      : alpha(
                                                          theme.palette.grey[400],
                                                          0.1
                                                        ),
                                                  borderRadius: 1,
                                                }}
                                              >
                                                <Box sx={{ mr: 2 }}>
                                                  {parsedIntelligence?.analysis
                                                    .sentimentOverall ===
                                                  "positive" ? (
                                                    <MoodIcon
                                                      fontSize="large"
                                                      sx={{
                                                        color:
                                                          theme.palette.success
                                                            .main,
                                                      }}
                                                    />
                                                  ) : parsedIntelligence?.analysis
                                                      .sentimentOverall ===
                                                    "negative" ? (
                                                    <MoodIcon
                                                      fontSize="large"
                                                      sx={{
                                                        color:
                                                          theme.palette.error.main,
                                                        transform: "rotate(180deg)",
                                                      }}
                                                    />
                                                  ) : (
                                                    <MoodIcon
                                                      fontSize="large"
                                                      sx={{
                                                        color:
                                                          theme.palette.grey[500],
                                                      }}
                                                    />
                                                  )}
                                                </Box>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontWeight: "medium",
                                                    color:
                                                      parsedIntelligence?.analysis
                                                        .sentimentOverall ===
                                                      "positive"
                                                        ? theme.palette.success.main
                                                        : parsedIntelligence
                                                            ?.analysis
                                                            .sentimentOverall ===
                                                          "negative"
                                                        ? theme.palette.error.main
                                                        : theme.palette.text
                                                            .primary,
                                                  }}
                                                >
                                                  {parsedIntelligence?.analysis.sentimentOverall
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                    parsedIntelligence?.analysis.sentimentOverall.slice(
                                                      1
                                                    )}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          )}

                                          {/* BANT Analysis */}
                                          {parsedIntelligence?.analysis
                                            ?.bantAnalysis && (
                                            <>
                                              <Typography
                                                variant="subtitle2"
                                                sx={{
                                                  mt: 2,
                                                  fontWeight: "bold",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                }}
                                              >
                                                <BusinessIcon fontSize="small" />{" "}
                                                BANT Status
                                              </Typography>
                                              <Box
                                                sx={{
                                                  display: "grid",
                                                  gridTemplateColumns: "1fr 1fr",
                                                  gap: 2,
                                                  mt: 1,
                                                  bgcolor:
                                                    theme.palette.background.paper,
                                                  borderRadius: 1,
                                                  p: 1.5,
                                                }}
                                              >
                                                {/* Budget */}
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor:
                                                      parsedIntelligence?.analysis
                                                        .bantAnalysis.budget ===
                                                      "Yes"
                                                        ? alpha(
                                                            theme.palette.success
                                                              .main,
                                                            0.1
                                                          )
                                                        : alpha(
                                                            theme.palette.grey[300],
                                                            0.2
                                                          ),
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      mb: 0.5,
                                                    }}
                                                  >
                                                    <AttachMoneyIcon
                                                      fontSize="small"
                                                      sx={{ mr: 0.5 }}
                                                    />
                                                    <Typography variant="subtitle2">
                                                      Budget
                                                    </Typography>
                                                  </Box>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontWeight:
                                                        parsedIntelligence?.analysis
                                                          .bantAnalysis.budget ===
                                                        "Yes"
                                                          ? "bold"
                                                          : "normal",
                                                      color:
                                                        parsedIntelligence?.analysis
                                                          .bantAnalysis.budget ===
                                                        "Yes"
                                                          ? theme.palette.success
                                                              .main
                                                          : theme.palette.text
                                                              .secondary,
                                                    }}
                                                  >
                                                    {parsedIntelligence?.analysis
                                                      .bantAnalysis.budget ||
                                                      "Unknown"}
                                                  </Typography>
                                                  {parsedIntelligence?.analysis
                                                    .bantAnalysis.budgetDetail && (
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                    >
                                                      {
                                                        parsedIntelligence?.analysis
                                                          .bantAnalysis.budgetDetail
                                                      }
                                                    </Typography>
                                                  )}
                                                </Box>

                                                {/* Authority */}
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor:
                                                      parsedIntelligence?.analysis
                                                        .bantAnalysis.authority ===
                                                      "Yes"
                                                        ? alpha(
                                                            theme.palette.success
                                                              .main,
                                                            0.1
                                                          )
                                                        : alpha(
                                                            theme.palette.grey[300],
                                                            0.2
                                                          ),
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      mb: 0.5,
                                                    }}
                                                  >
                                                    <SupervisorAccountIcon
                                                      fontSize="small"
                                                      sx={{ mr: 0.5 }}
                                                    />
                                                    <Typography variant="subtitle2">
                                                      Authority
                                                    </Typography>
                                                  </Box>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontWeight:
                                                        parsedIntelligence?.analysis
                                                          .bantAnalysis
                                                          .authority === "Yes"
                                                          ? "bold"
                                                          : "normal",
                                                      color:
                                                        parsedIntelligence?.analysis
                                                          .bantAnalysis
                                                          .authority === "Yes"
                                                          ? theme.palette.success
                                                              .main
                                                          : theme.palette.text
                                                              .secondary,
                                                    }}
                                                  >
                                                    {parsedIntelligence?.analysis
                                                      .bantAnalysis.authority ||
                                                      "Unknown"}
                                                  </Typography>
                                                  {parsedIntelligence?.analysis
                                                    .bantAnalysis
                                                    .authorityDetail && (
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                    >
                                                      {
                                                        parsedIntelligence?.analysis
                                                          .bantAnalysis
                                                          .authorityDetail
                                                      }
                                                    </Typography>
                                                  )}
                                                </Box>

                                                {/* Need */}
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor:
                                                      parsedIntelligence?.analysis
                                                        .bantAnalysis.need === "Yes"
                                                        ? alpha(
                                                            theme.palette.success
                                                              .main,
                                                            0.1
                                                          )
                                                        : alpha(
                                                            theme.palette.grey[300],
                                                            0.2
                                                          ),
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      mb: 0.5,
                                                    }}
                                                  >
                                                    <NeedIcon
                                                      fontSize="small"
                                                      sx={{ mr: 0.5 }}
                                                    />
                                                    <Typography variant="subtitle2">
                                                      Need
                                                    </Typography>
                                                  </Box>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontWeight:
                                                        parsedIntelligence?.analysis
                                                          .bantAnalysis.need ===
                                                        "Yes"
                                                          ? "bold"
                                                          : "normal",
                                                      color:
                                                        parsedIntelligence?.analysis
                                                          .bantAnalysis.need ===
                                                        "Yes"
                                                          ? theme.palette.success
                                                              .main
                                                          : theme.palette.text
                                                              .secondary,
                                                    }}
                                                  >
                                                    {parsedIntelligence?.analysis
                                                      .bantAnalysis.need ||
                                                      "Unknown"}
                                                  </Typography>
                                                  {parsedIntelligence?.analysis
                                                    .bantAnalysis.needDetail && (
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                    >
                                                      {
                                                        parsedIntelligence?.analysis
                                                          .bantAnalysis.needDetail
                                                      }
                                                    </Typography>
                                                  )}
                                                </Box>

                                                {/* Timeline */}
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    p: 1,
                                                    borderRadius: 1,
                                                    bgcolor:
                                                      parsedIntelligence?.analysis
                                                        .bantAnalysis.timeline ===
                                                      "Yes"
                                                        ? alpha(
                                                            theme.palette.success
                                                              .main,
                                                            0.1
                                                          )
                                                        : alpha(
                                                            theme.palette.grey[300],
                                                            0.2
                                                          ),
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      mb: 0.5,
                                                    }}
                                                  >
                                                    <AccessTimeIcon
                                                      fontSize="small"
                                                      sx={{ mr: 0.5 }}
                                                    />
                                                    <Typography variant="subtitle2">
                                                      Timeline
                                                    </Typography>
                                                  </Box>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontWeight:
                                                        parsedIntelligence?.analysis
                                                          .bantAnalysis.timeline ===
                                                        "Yes"
                                                          ? "bold"
                                                          : "normal",
                                                      color:
                                                        parsedIntelligence?.analysis
                                                          .bantAnalysis.timeline ===
                                                        "Yes"
                                                          ? theme.palette.success
                                                              .main
                                                          : theme.palette.text
                                                              .secondary,
                                                    }}
                                                  >
                                                    {parsedIntelligence?.analysis
                                                      .bantAnalysis.timeline ||
                                                      "Unknown"}
                                                  </Typography>
                                                  {parsedIntelligence?.analysis
                                                    .bantAnalysis
                                                    .timelineDetail && (
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                    >
                                                      {
                                                        parsedIntelligence?.analysis
                                                          .bantAnalysis
                                                          .timelineDetail
                                                      }
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </Box>
                                            </>
                                          )}
                                        </>
                                      )}
                                    </Box>
                                  )}

                                  {/* Memories/Knowledge Graph Tab */}
                                  {tabValue === 1 && (
                                    <Box>
                                      <MemoryAndKnowledgeGraph
                                      selectedCustomer={conversation?.customer}
                                      conversation={conversation}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </>
                            );
                          })()}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </Box>
      ))}

      {hasNextPage && (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            variant="contained"
          >
            {isFetchingNextPage ? "Loading more..." : "Load More"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ConversationsList;
